import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { Subsets, subsetSetter, subsetGetter, SubsetGetterProps } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Idrott = {
    id: string;
    namn: string;
};

type State = BaseState & {
    idrott: Map<Idrott["id"], Idrott>;
};

const getDefaultState: () => State = () => ({
    idrott: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "idrott";
const baseUrl = "skreg/idrotter/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        idrott: (state: State) => [...state.idrott.values()],
        idrottById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) return null;
                return state.idrott.get(id);
            },
        idrottSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({ ...props, state, collectionName: "idrott" }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addIdrott: (
            state: State,
            {
                offset,
                subsetName = "default",
                idrott = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                idrott: Idrott[];
            }
        ) =>
            subsetSetter<State, Idrott>({
                offset,
                subsetName,
                state,
                collectionName: "idrott",
                items: idrott,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        fetchIdrott: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams = {},
                forceRefresh = false,
            }: {
                limit?: number;
                page?: number;
                filterParams?: FilterParams;
                forceRefresh?: boolean;
            } = {}
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Idrott>({
                    filterParams,
                    state,
                    page,
                    limit,
                    forceRefresh,
                    collectionName: "idrott",
                    apiUrl: baseUrl,
                });

                if (results) {
                    commit("addIdrott", { subsetName, offset, idrott: results });
                }
                if (typeof total === "number") {
                    commit("setTotal", { total, subsetName });
                }

                return { subsetName, total, nextPage };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
